import React, { useMemo } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { checklistAnswerErrorsByAnswerId } from 'views/template/wizard/form/validation';
import { MemoComplianceAnswerPanel } from './ComplianceAnswerPanel';
import { MemoNumericAnswerPanel } from './NumericAnswerPanel';
import { MemoTextAnswerPanel } from './TextAnswerPanel';
import { MemoOptionSelectionAnswerPanel } from './OptionSelectionAnswerPanel';
import { MemoAddAnswerMenu } from './AddAnswerMenu';
import { AnswerConfig, ChecklistAnswerFieldErrors } from './types';

interface AnswerConfigListProps {
  answers: AnswerConfig[];
  answerErrors?: ChecklistAnswerFieldErrors[];
  onAddAnswer: (answerType: AnswerType) => void;
  onUpdateAnswer: (answerId: string, updates: Partial<AnswerConfig>) => void;
  onDeleteAnswer: (answerId: string) => void;
}

type AnswerConfigPanelItemProps = {
  answer: AnswerConfig;
  fieldErrors?: ChecklistAnswerFieldErrors;
  onUpdateAnswer: AnswerConfigListProps['onUpdateAnswer'];
  onDeleteAnswer: AnswerConfigListProps['onDeleteAnswer'];
};

function AnswerConfigPanelItem({
  answer,
  fieldErrors,
  onUpdateAnswer,
  onDeleteAnswer,
}: AnswerConfigPanelItemProps): React.ReactElement | null {
  const answerId = answer._id || answer.uniqueKeyId;

  switch (answer.type) {
    case AnswerType.Compliance:
      return (
        <MemoComplianceAnswerPanel
          answerId={answerId}
          answer={answer}
          fieldErrors={fieldErrors}
          onUpdate={onUpdateAnswer}
          onDelete={onDeleteAnswer}
        />
      );
    case AnswerType.Numeric:
      return (
        <MemoNumericAnswerPanel
          answerId={answerId}
          answer={answer}
          fieldErrors={fieldErrors}
          onUpdate={onUpdateAnswer}
          onDelete={onDeleteAnswer}
        />
      );
    case AnswerType.Text:
      return (
        <MemoTextAnswerPanel
          answerId={answerId}
          answer={answer}
          fieldErrors={fieldErrors}
          onUpdate={onUpdateAnswer}
          onDelete={onDeleteAnswer}
        />
      );
    case AnswerType.OptionSelection:
      return (
        <MemoOptionSelectionAnswerPanel
          answerId={answerId}
          answer={answer}
          fieldErrors={fieldErrors}
          onUpdate={onUpdateAnswer}
          onDelete={onDeleteAnswer}
        />
      );
    default:
      return null;
  }
}

const MemoAnswerConfigPanelItem = React.memo(AnswerConfigPanelItem);

export function AnswerConfigList({
  answers,
  answerErrors,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
}: AnswerConfigListProps): React.ReactElement {
  const intl = useIntl();

  const answerErrorsByAnswerId = useMemo(
    () => checklistAnswerErrorsByAnswerId(answers, answerErrors),
    [answers, answerErrors]
  );

  return (
    <Stack spacing={2}>
      {answers.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          {formatTranslation(intl, InspectionTranslationKeys.noAnswers)}
        </Typography>
      ) : (
        answers.map((answer) => (
          <MemoAnswerConfigPanelItem
            key={answer._id || answer.uniqueKeyId}
            answer={answer}
            fieldErrors={answerErrorsByAnswerId.get(
              answer._id || answer.uniqueKeyId
            )}
            onUpdateAnswer={onUpdateAnswer}
            onDeleteAnswer={onDeleteAnswer}
          />
        ))
      )}
      <MemoAddAnswerMenu onAddAnswer={onAddAnswer} />
    </Stack>
  );
}

export const MemoAnswerConfigList = React.memo(AnswerConfigList);
